import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import {
  Grid,
  Card,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import LoaderOverlay from "components/LoaderOverlay";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { api } from "../../api";
import { patientEditSchema } from "./patientEditSchema";

const selectBoxHeight = 45; // Set the height for the select box

function PatientEdit() {
  const { patient_code, couple_code } = useParams();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [patientData, setPatientData] = useState(null);
  const [contactId, setContactId] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(patientEditSchema),
    defaultValues: {
      first_name: "",
      middle_name: "",
      last_name: "",
      email_address: "",
      gender: "",
      occupation: "",
      religion: "",
      nationality: "",
      date_of_birth: "",
      married_for: "",
      trying_for: "",
      remarks: "",
    },
  });

  // Load patient details on component mount
  useEffect(() => {
    const loadPatientData = async () => {
      if (!patient_code) {
        setError("Patient code is required");
        return;
      }

      setLoading(true);
      setError("");

      try {
        // Get patient details using patient_code
        const detailsResponse = await api.contacts.getPatientDetails(patient_code);
        
        if (detailsResponse?.data?.contact) {
          const contact = detailsResponse.data.contact;
          setPatientData(contact);
          setContactId(contact.id); // Store contact ID for form submission
          
          // Populate form with existing data
          reset({
            first_name: contact.first_name || "",
            middle_name: contact.middle_name || "",
            last_name: contact.last_name || "",
            email_address: contact.email_address || "",
            gender: contact.gender || "",
            occupation: contact.occupation || "",
            religion: contact.religion || "",
            nationality: contact.nationality || "",
            date_of_birth: contact.date_of_birth ? contact.date_of_birth.split('T')[0] : "",
            married_for: contact.married_for || "",
            trying_for: contact.trying_for || "",
            remarks: contact.remarks || "",
          });
        } else {
          setError("Patient details not found");
        }
      } catch (error) {
        console.error("Error loading patient data:", error);
        setError(error.response?.data?.message || "Failed to load patient data");
      } finally {
        setLoading(false);
      }
    };

    loadPatientData();
  }, [patient_code, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Prepare payload - only send fields that have been provided
      const payload = {};
      
      if (data.first_name) payload.first_name = data.first_name;
      if (data.middle_name) payload.middle_name = data.middle_name;
      if (data.last_name) payload.last_name = data.last_name;
      if (data.email_address) payload.email_address = data.email_address;
      if (data.gender) payload.gender = data.gender;
      if (data.occupation) payload.occupation = data.occupation;
      if (data.religion) payload.religion = data.religion;
      if (data.nationality) payload.nationality = data.nationality;
      if (data.date_of_birth) payload.date_of_birth = data.date_of_birth;
      if (data.married_for) payload.married_for = data.married_for;
      if (data.trying_for) payload.trying_for = data.trying_for;
      if (data.remarks !== undefined) payload.remarks = data.remarks;

      // Call the edit API
      const response = await api.contacts.editContact(contactId, payload);

      if (response?.data) {
        setSuccess("Patient information updated successfully!");
        
        // Navigate back to patient list after 2 seconds
        setTimeout(() => {
          navigate("/patient-list");
        }, 2000);
      }
    } catch (error) {
      console.error("Error updating patient:", error);
      setError(error.response?.data?.message || "Failed to update patient information");
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/patient-list");
  };

  if (loading && !patientData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <LoaderOverlay open={loading} />
        <MDBox pt={6} pb={3}>
          <Typography variant="h6">Loading patient data...</Typography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                p={3}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box display="flex" alignItems="center">
                  <IconButton onClick={handleGoBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <MDTypography variant="h4">Edit Patient Information</MDTypography>
                </Box>
              </MDBox>
            </Card>
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            </Grid>
          )}

          {success && (
            <Grid item xs={12}>
              <Alert severity="success" onClose={() => setSuccess("")}>
                {success}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    {/* Personal Information Section */}
                    <Grid item xs={12}>
                      <MDTypography variant="h6" mb={2}>
                        Personal Information
                      </MDTypography>
                    </Grid>

                    {/* First Name */}
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="first_name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="First Name"
                            fullWidth
                            error={!!errors.first_name}
                            helperText={errors.first_name?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Middle Name */}
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="middle_name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Middle Name"
                            fullWidth
                            error={!!errors.middle_name}
                            helperText={errors.middle_name?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Last Name */}
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="last_name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Last Name"
                            fullWidth
                            error={!!errors.last_name}
                            helperText={errors.last_name?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="email_address"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Email Address"
                            type="email"
                            fullWidth
                            error={!!errors.email_address}
                            helperText={errors.email_address?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Gender */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="gender"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth margin="normal" error={!!errors.gender}>
                            <InputLabel id="gender-label">Gender</InputLabel>
                            <Select
                              {...field}
                              labelId="gender-label"
                              label="Gender"
                              sx={{
                                height: selectBoxHeight,
                              }}
                            >
                              <MenuItem value="F">Female</MenuItem>
                              <MenuItem value="M">Male</MenuItem>
                            </Select>
                            <Typography color="error" variant="caption">
                              {errors.gender?.message}
                            </Typography>
                          </FormControl>
                        )}
                      />
                    </Grid>

                    {/* Date of Birth */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="date_of_birth"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Date of Birth"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.date_of_birth}
                            helperText={errors.date_of_birth?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Occupation */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="occupation"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Occupation"
                            fullWidth
                            error={!!errors.occupation}
                            helperText={errors.occupation?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Religion */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="religion"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Religion"
                            fullWidth
                            error={!!errors.religion}
                            helperText={errors.religion?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Nationality */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="nationality"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Nationality"
                            fullWidth
                            error={!!errors.nationality}
                            helperText={errors.nationality?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Medical Information Section */}
                    <Grid item xs={12} mt={3}>
                      <MDTypography variant="h6" mb={2}>
                        Medical Information
                      </MDTypography>
                    </Grid>

                    {/* Married For */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="married_for"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Married For"
                            fullWidth
                            placeholder="e.g., 5 years"
                            error={!!errors.married_for}
                            helperText={errors.married_for?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Trying For */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="trying_for"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Trying For"
                            fullWidth
                            placeholder="e.g., 2 years"
                            error={!!errors.trying_for}
                            helperText={errors.trying_for?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Remarks */}
                    <Grid item xs={12}>
                      <Controller
                        name="remarks"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Remarks"
                            fullWidth
                            multiline
                            rows={4}
                            error={!!errors.remarks}
                            helperText={errors.remarks?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                      <MDBox display="flex" justifyContent="center" mt={4} width="100%">
                        <button
                          type="submit"
                          disabled={loading}
                          style={{
                            padding: '10px 24px',
                            background: loading ? '#cccccc' : '#00798E',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            transition: 'background 0.3s ease',
                          }}
                          onMouseOver={(e) => {
                            if (!loading) e.currentTarget.style.background = '#006379';
                          }}
                          onMouseOut={(e) => {
                            if (!loading) e.currentTarget.style.background = '#00798E';
                          }}
                        >
                          <SaveIcon fontSize="small" />
                          {loading ? "Saving..." : "Save Changes"}
                        </button>
                      </MDBox>
                    </Grid>
                  </Grid>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <LoaderOverlay open={loading} />
      <Footer />
    </DashboardLayout>
  );
}

export default PatientEdit;
